import React, { useState, useEffect } from 'react';
import { 
  Home, 
  Users, 
  UserPlus, 
  Search, 
  Bell,
  Settings,
  Wallet,
  LogOut
} from 'lucide-react';

const MainNavigation = ({ currentPage = 'dashboard', onNavigate, user, onLogout }) => {
  const [activePage, setActivePage] = useState(currentPage);
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);

  // Synchroniser l'état local avec le prop currentPage
  useEffect(() => {
    setActivePage(currentPage);
  }, [currentPage]);

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Accueil',
      icon: Home,
      description: 'Tableau de bord principal'
    },
    {
      id: 'join-requests',
      label: 'Adhésion',
      icon: UserPlus,
      description: 'Demandes d\'adhésion aux groupes'
    },
    {
      id: 'my-groups',
      label: 'Mes Groupes',
      icon: Users,
      description: 'Groupes dont vous êtes membre'
    },
    {
      id: 'explore',
      label: 'Explorer',
      icon: Search,
      description: 'Rechercher et découvrir des groupes'
    },
    {
      id: 'crypto',
      label: 'Crypto',
      icon: Wallet,
      description: 'Portefeuille et transactions crypto'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Alertes et messages'
    },
    {
      id: 'settings',
      label: 'Paramètres',
      icon: Settings,
      description: 'Paramètres et déconnexion'
    }
  ];

  const handleNavigation = (pageId) => {
    if (pageId === 'settings') {
      setShowLogoutMenu(false); // Fermer le menu s'il était ouvert
      setActivePage(pageId);
      if (onNavigate) {
        onNavigate(pageId);
      }
      return;
    }
    
    setActivePage(pageId);
    if (onNavigate) {
      onNavigate(pageId);
    }
  };

  const handleLogout = () => {
    setShowLogoutMenu(false);
    if (onLogout) {
      onLogout();
    } else {
      // Fallback logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('groups');
      sessionStorage.clear();
      alert('✅ Vous êtes déconnecté·e avec succès');
      window.location.reload();
    }
  };

  return (
    <>
      {/* Bottom Navigation Bar - Mobile First */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 md:hidden safe-area-inset-bottom">
        <div className="grid grid-cols-4 sm:grid-cols-5 h-16 relative overflow-x-auto">
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = activePage === item.id || (item.id === 'settings' && showLogoutMenu);
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.id)}
                className={`flex flex-col items-center justify-center p-2 transition-colors duration-200 relative ${
                  isActive 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-600 hover:text-blue-500 hover:bg-gray-50'
                }`}
                aria-label={item.description}
                title={item.label}
              >
                <IconComponent 
                  size={20} 
                  className={`mb-1 ${isActive ? 'text-blue-600' : 'text-gray-600'}`}
                />
                <span className={`text-xs font-medium ${
                  isActive ? 'text-blue-600' : 'text-gray-600'
                }`}>
                  {item.label}
                </span>
                
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-blue-600 rounded-b-full"></div>
                )}
              </button>
            );
          })}
          
          {/* Logout Menu Popup - Only show when not on settings page */}
          {showLogoutMenu && activePage !== 'settings' && (
            <div className="absolute bottom-full right-2 mb-2 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-[70] min-w-[200px]">
              <button
                onClick={() => {
                  handleNavigation('settings'); 
                  setShowLogoutMenu(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-left mb-2"
              >
                <Settings size={20} />
                <div>
                  <p className="font-medium">Paramètres</p>
                  <p className="text-xs text-gray-500">Configuration de l'app</p>
                </div>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
              >
                <LogOut size={20} />
                <div>
                  <p className="font-medium">Se déconnecter</p>
                  <p className="text-xs text-gray-500">Quitter l'application</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Sidebar Navigation - Hidden on mobile */}
      <div className="hidden md:flex md:fixed md:left-0 md:top-0 md:bottom-0 md:w-64 md:bg-white md:border-r md:border-gray-200 md:shadow-lg md:z-40">
        <div className="flex flex-col w-full p-4">
          {/* User Info */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg text-white">
            <h3 className="font-bold text-lg">Karibu, {user?.username}!</h3>
            <p className="text-sm text-purple-100">
              {user?.role === 'admin' ? '👑 Administrateur' : '👤 Membre'}
            </p>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1">
            <ul className="space-y-2">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = activePage === item.id;
                
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleNavigation(item.id)}
                      className={`w-full flex items-center p-3 rounded-lg transition-colors duration-200 ${
                        isActive 
                          ? 'bg-blue-600 text-white shadow-lg' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      aria-label={item.description}
                    >
                      <IconComponent 
                        size={20} 
                        className={`mr-3 ${isActive ? 'text-white' : 'text-gray-500'}`}
                      />
                      <div className="flex-1 text-left">
                        <div className={`font-medium ${
                          isActive ? 'text-white' : 'text-gray-900'
                        }`}>
                          {item.label}
                        </div>
                        <div className={`text-xs ${
                          isActive ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {item.description}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>

      {/* Content Spacer - Add bottom padding on mobile to account for fixed nav */}
      <div className="pb-16 md:pb-0 md:ml-64" />
    </>
  );
};

export default MainNavigation;